// minimap-view.ts — the in-game RADAR (minimap) as a DOM <canvas> overlay (HTML/DOM UI
// shell plan, Phase 2 = forms.ini elimination). It REPLACES the canvas MiniMapControl
// (deleted): a pixel-faithful radar that no longer needs forms.ini.
//
// It owns a <canvas> sized exactly to the minimap dib (mapTiles × nScale px), presented
// at × kGameScale with image-rendering:pixelated (the canvas MiniMapControl drew its dib
// at native size and the back buffer was presented scaled by kGameScale — the SAME
// factor → identical CSS size). Anchored bottom-RIGHT, sitting directly ABOVE the HUD
// bottom bar (the canvas anchored it bottom-right of the sim strip, i.e. just above the
// input console strip — game.cpp:2084-2105). pointer-events:auto: taps/drags over the
// radar are click-to-jump / drag-scroll (the canvas MiniMapControl::OnPenEvent2), NOT
// map gestures.
//
// Per frame the host pushes a MiniMapModel (the clean sim/tilemap getters the canvas
// control read). The view renders it through the PURE painter (minimap-render.ts:
// createMiniMapSurface + redrawAll) into a CpuDibBitmap, draws the white viewport box
// (re-render each frame — it tracks scroll), then putImageData to the canvas. The
// content-dirty flag (set by the sim's MiniMapRedraw* host seams, coalesced) gates the
// terrain/blip recompute; the viewport box + the radar bool are cheap and re-evaluated
// every push. DISPLAY-only — the only sim mutation is setViewPos (the canvas did the same).

import { CpuDibBitmap } from '../../gfx/dibbitmap.ts';
import { GetColor, iclr } from '../../gfx/palette.ts';
import {
  createMiniMapSurface,
  redrawAll,
  redrawTRect,
  viewportBox,
  minimapPenDown,
  minimapPenMove,
  type MiniMapData,
  type MiniMapSurface,
  type MiniMapViewGeom,
  type MiniMapInputGeom,
  type MiniMapPenState,
  type RectLike,
} from './minimap-render.ts';

/** The per-frame model the host (GameSession) pushes — every field is a clean sim/
 *  tilemap getter the canvas MiniMapControl already read. */
export interface MiniMapModel {
  /** the tile data bundle for the pure painter (rebuilt only when the surface is
   *  (re)created — the arrays are stable references; the host returns the same object). */
  data: MiniMapData;
  /** the per-(map,scale) surface inputs (mirrors MiniMapControl::Init geometry). */
  surface: {
    sizMapCx: number;
    sizMapCy: number;
    gcxTile: number;
    gcyTile: number;
    cyInputUI: number;
    fMultiplayer: boolean;
    sideLocal: number;
  };
  /** the viewport-box geometry (the white box tracking scroll). */
  view: MiniMapViewGeom;
  /** the input geometry (click-to-jump / drag-scroll). */
  input: MiniMapInputGeom;
  /** true if the radar content changed since the last push (the coalesced dirty flag) —
   *  the view recomputes the terrain/blip dib only then (the viewport box is always
   *  cheap). The first push always renders. */
  dirty: boolean;
  /** when dirty: the accumulated dirty TILE rect (half-open) the sim's tile-precise
   *  seams reported, or null for a full redraw (MiniMapRedraw/CalcPoweredRadar/first
   *  push). The view repaints just this rect — a unit stepping one tile no longer
   *  costs a whole-map rescan. */
  dirtyRect: { left: number; top: number; right: number; bottom: number } | null;
}

export interface MiniMapViewDeps {
  /** the overlay root (created by the host; this view owns its contents). */
  root: HTMLElement;
  /** the canvas magnification (kGameScale) — the dib is presented at × this, matching
   *  the canvas MiniMapControl (drawn native, back buffer presented scaled). */
  magnify: number;
  /** the REAL DOM HUD bar height in CSS px (the radar sits directly above it). The host
   *  passes consoleStripHeight × the CHROME magnify (kUiMagnify) — the actual rendered bar
   *  height, which on phones is TALLER than the canvas input strip (consoleStripHeight ×
   *  kGameScale). Anchoring off kGameScale here would leave the radar overlapping the bar. */
  hudBarPx: number;
  /** the new view world position → sui.SetViewPos (the SAME seam the canvas used). */
  setViewPos: (wx: number, wy: number) => void;
}

const COL_WHITE = GetColor(iclr.kiclrWhite);

export class MiniMapView {
  private readonly d: MiniMapViewDeps;
  private readonly canvas: HTMLCanvasElement;
  private readonly cctx: CanvasRenderingContext2D;
  /** the owned dib (the pure painter target) + its layout — (re)created when the map/
   *  scale changes (dib size differs). */
  private surf: MiniMapSurface | null = null;
  private surfKey = '';
  private nScale = 1;
  /** the active drag's pen statics (null = no drag in progress). */
  private pen: MiniMapPenState | null = null;
  private last: MiniMapModel | null = null;

  constructor(d: MiniMapViewDeps) {
    this.d = d;
    const s = d.root.style;
    s.position = 'fixed';
    s.right = '0';
    // sit directly ABOVE the HUD bottom bar (bottom-right of the playfield/sim strip).
    s.bottom = `${Math.round(d.hudBarPx)}px`;
    s.zIndex = '11'; // above the canvas + the HUD bar (z=10), below modals (z=20)
    s.pointerEvents = 'auto'; // the radar handles its own taps/drags (not the map)
    s.lineHeight = '0';
    s.userSelect = 'none';
    s.touchAction = 'none'; // we drive scroll ourselves; suppress browser panning

    this.canvas = document.createElement('canvas');
    this.canvas.id = 'dom-minimap-canvas';
    this.canvas.style.display = 'block';
    this.canvas.style.imageRendering = 'pixelated';
    this.cctx = this.canvas.getContext('2d', { willReadFrequently: false })!;
    d.root.replaceChildren(this.canvas);

    this.canvas.addEventListener('pointerdown', this.onPointerDown);
    this.canvas.addEventListener('pointermove', this.onPointerMove);
    this.canvas.addEventListener('pointerup', this.onPointerUp);
    this.canvas.addEventListener('pointercancel', this.onPointerUp);
    window.addEventListener('resize', this.onResize);
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  /** iOS Safari can drop a background tab's 2D canvas backing store; the partial-blit
   *  path would then only ever restore small dirty rects. Force one full re-blit on
   *  return to visible (the staging ImageData still mirrors the dib — cheap). */
  private blitAll = false;
  private readonly onVisibility = (): void => {
    if (!document.hidden) this.blitAll = true;
  };

  destroy(): void {
    this.canvas.removeEventListener('pointerdown', this.onPointerDown);
    this.canvas.removeEventListener('pointermove', this.onPointerMove);
    this.canvas.removeEventListener('pointerup', this.onPointerUp);
    this.canvas.removeEventListener('pointercancel', this.onPointerUp);
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.d.root.replaceChildren();
    this.surf = null;
    this.last = null;
  }

  private readonly onResize = (): void => {
    // bottom offset is in CSS px (constant per game); nothing to recompute on resize —
    // the canvas keeps its pixelated CSS size. (kept for symmetry with hud-view.)
  };

  /** (re)create the owned dib + its border layout when the map/scale changes. */
  private ensureSurface(m: MiniMapModel): void {
    const key = `${m.data.ctx}x${m.data.cty}@${m.data.nScale}`;
    if (this.surf !== null && key === this.surfKey) return;
    this.nScale = m.data.nScale;
    this.surf = createMiniMapSurface(
      m.data.ctx,
      m.data.cty,
      m.data.nScale,
      m.surface.sizMapCx,
      m.surface.sizMapCy,
      m.surface.gcxTile,
      m.surface.gcyTile,
      m.surface.cyInputUI,
      m.surface.fMultiplayer,
      m.surface.sideLocal,
      (cx, cy) => new CpuDibBitmap(cx, cy),
    );
    this.surfKey = key;
    // Size the canvas to the ON-SCREEN control rect (dib + borders), presented × magnify
    // (pixelated). MiniMapControl::OnPaint (origin/master simuiform.ts:1161-1190):
    //   vertBorderInside===true  → the top+left borders are baked INTO the dib, so the
    //     control rect == the dib (siz.cx × siz.cy); blit at (0,0).
    //   vertBorderInside===false → only the top border is in the dib (cxDib = rcWidth -
    //     cxyBorder, height = rcHeight); the left/right/bottom borders are drawn OUTSIDE,
    //     so the control rect is wider+taller: width = cxDib + 2*cxyBorder, height =
    //     rcHeight + cxyBorder. (siz.cx=cxDib, siz.cy=rcHeight, rcWidth=cxDib+cxyBorder.)
    const siz = this.surf.pbm.GetSize();
    const b = this.surf.cxyBorder;
    const cw = this.surf.vertBorderInside ? siz.cx : siz.cx + 2 * b;
    const ch = this.surf.vertBorderInside ? siz.cy : siz.cy + b;
    this.canvas.width = cw;
    this.canvas.height = ch;
    this.canvas.style.width = `${Math.round(cw * this.d.magnify)}px`;
    this.canvas.style.height = `${Math.round(ch * this.d.magnify)}px`;
    // the persistent blit staging buffer — mirrors the dib after every content
    // refresh, so per-frame pushes never allocate an ImageData again
    this.img = new ImageData(siz.cx, siz.cy);
    this.lastBox = null;
    // re-render the whole map next paint
    this.contentValid = false;
  }

  private contentValid = false;
  private img: ImageData | null = null;
  /** the white viewport box as last drawn (dib px) — a push whose content AND box are
   *  both unchanged is a no-op (the common idle/no-tick rAF). */
  private lastBox: RectLike | null = null;

  /** the per-frame push from the host. Recomputes the terrain/blip dib only where
   *  dirty (the accumulated tile-rect, or everything on a full-dirty push), then blits
   *  only the union of what changed — changed content ∪ old box ∪ new box. A push with
   *  clean content and an unmoved box does no canvas work at all (this runs on every
   *  rAF, up to 120Hz, so the idle path must be free). */
  update(m: MiniMapModel): void {
    this.ensureSurface(m);
    const surf = this.surf!;
    const siz = surf.pbm.GetSize();
    const img = this.img!;
    const nScale = this.nScale;

    // (1) terrain/unit/wall/galaxite/fog — recompute the dirty part of the dib
    let contentFull = false;
    let contentPx: RectLike | null = null; // changed dib region, dib px
    if (!this.contentValid || (m.dirty && m.dirtyRect === null)) {
      redrawAll(m.data, surf);
      this.contentValid = true;
      contentFull = true;
    } else if (m.dirty && m.dirtyRect !== null) {
      // clamp the accumulated tile rect to the map, repaint just it
      const trc = {
        left: Math.max(0, m.dirtyRect.left),
        top: Math.max(0, m.dirtyRect.top),
        right: Math.min(m.data.ctx, m.dirtyRect.right),
        bottom: Math.min(m.data.cty, m.dirtyRect.bottom),
      };
      if (trc.right > trc.left && trc.bottom > trc.top) {
        redrawTRect(m.data, surf, trc);
        // tile → dib px (fillDst offsets by xOff/yOff), clamped to the dib
        contentPx = {
          left: Math.min(siz.cx, surf.xOff + trc.left * nScale),
          top: Math.min(siz.cy, surf.yOff + trc.top * nScale),
          right: Math.min(siz.cx, surf.xOff + trc.right * nScale),
          bottom: Math.min(siz.cy, surf.yOff + trc.bottom * nScale),
        };
        if (contentPx.right <= contentPx.left || contentPx.bottom <= contentPx.top) {
          contentPx = null;
        }
      }
    }

    // (2) the white viewport box (tracks scroll — changes without a content-dirty)
    const box = viewportBox(m.view, surf, nScale);
    const lb = this.lastBox;
    const boxChanged =
      lb === null ||
      box.left !== lb.left ||
      box.top !== lb.top ||
      box.right !== lb.right ||
      box.bottom !== lb.bottom;

    const blitAll = contentFull || this.blitAll;
    this.blitAll = false;
    if (!blitAll && contentPx === null && !boxChanged) {
      this.last = m;
      return; // nothing visible changed — free idle frame
    }

    // (3) refresh the staging ImageData for the changed content (contentFull and
    // contentPx are mutually exclusive by construction above), then blit — full, or
    // the union of (changed content ∪ old box ∪ new box) in ONE putImageData. Mirror
    // the canvas MiniMapControl::OnPaint blit (origin/master simuiform.ts:1161-1190)
    // for the two vertBorderInside cases — putImageData ignores the 2D transform but
    // DOES honor its dx offset, so the FALSE-case +cxyBorder dib shift is a
    // putImageData dx.
    const rowBytes = siz.cx * 4;
    const dx = surf.vertBorderInside ? 0 : surf.cxyBorder;
    if (contentFull) {
      img.data.set(surf.pbm.rgba);
    } else if (contentPx !== null) {
      // full-width row refresh keeps the copy simple (a row is a few hundred bytes)
      const off = contentPx.top * rowBytes;
      img.data.set(surf.pbm.rgba.subarray(off, contentPx.bottom * rowBytes), off);
    }
    if (blitAll) {
      this.cctx.putImageData(img, dx, 0);
      if (!surf.vertBorderInside) {
        // FALSE: only the top border is in the dib; paint the three OUTSIDE borders
        // in clrBorder — LEFT, RIGHT, BOTTOM (OnPaint's Fills):
        //   LEFT   (x,            y,          cxyBorder,    m_rc.Height()+1)
        //   RIGHT  (x+rcWidth,    y,          cxyBorder,    m_rc.Height()+1)
        //   BOTTOM (x,            y+rcHeight, m_rc.Width(), cxyBorder)
        // with m_rc.Width()=rcWidth=cxDib+cxyBorder, m_rc.Height()=rcHeight=siz.cy.
        // They lie OUTSIDE the putImageData target region, so a full blit is the
        // only time they need repainting.
        const b = surf.cxyBorder;
        const cxDib = siz.cx;
        const rcHeight = siz.cy;
        const rcWidth = cxDib + b;
        const c = surf.clrBorder;
        this.cctx.fillStyle = `rgb(${c.r},${c.g},${c.b})`;
        this.cctx.fillRect(0, 0, b, rcHeight + b); // LEFT
        this.cctx.fillRect(b + cxDib, 0, b, rcHeight + b); // RIGHT
        this.cctx.fillRect(0, rcHeight, rcWidth, b); // BOTTOM
      }
    } else {
      // union of the regions the canvas must re-show, in DIB coords (the box rects
      // are canvas coords — the FALSE-case blit shifts the dib +dx, so convert back)
      let ux0 = siz.cx;
      let uy0 = siz.cy;
      let ux1 = 0;
      let uy1 = 0;
      const grow = (rc: RectLike, xShift: number): void => {
        ux0 = Math.min(ux0, Math.max(0, rc.left - xShift));
        uy0 = Math.min(uy0, Math.max(0, rc.top));
        ux1 = Math.max(ux1, Math.min(siz.cx, rc.right - xShift));
        uy1 = Math.max(uy1, Math.min(siz.cy, rc.bottom));
      };
      if (contentPx !== null) grow(contentPx, 0);
      if (boxChanged && lb !== null) grow(lb, dx);
      grow(box, dx);
      if (ux1 > ux0 && uy1 > uy0) {
        this.cctx.putImageData(img, dx, 0, ux0, uy0, ux1 - ux0, uy1 - uy0);
      }
    }
    this.drawViewportBox(box);
    this.lastBox = box; // viewportBox returns a fresh rect — safe to retain

    this.last = m;
  }

  /** the white 1px viewport box (MiniMapControl::OnPaint DrawBorder), drawn straight onto
   *  the 2D context (it overlays the blitted dib; re-rendered on box/content change). */
  private drawViewportBox(rc: RectLike): void {
    const w = rc.right - rc.left;
    const h = rc.bottom - rc.top;
    if (w <= 0 || h <= 0) return;
    this.cctx.fillStyle = `rgb(${COL_WHITE.r},${COL_WHITE.g},${COL_WHITE.b})`;
    // DrawBorder(pbm, rc, 1, white) — a 1px frame (top/bottom/left/right edges).
    this.cctx.fillRect(rc.left, rc.top, w, 1);
    this.cctx.fillRect(rc.left, rc.bottom - 1, w, 1);
    this.cctx.fillRect(rc.left, rc.top, 1, h);
    this.cctx.fillRect(rc.right - 1, rc.top, 1, h);
  }

  // ── input: convert the DOM pointer CSS-px to MINIMAP-pixel coords (the C++ xPen/yPen),
  // then run the pure click-to-jump / drag-scroll → setViewPos. The canvas computed
  // xPen = pevt.x - (rcForm.left + m_rc.left + m_cxyBorder); here the canvas element's
  // top-left IS the dib origin, so xPen = (clientX - rect.left)/magnify - cxyBorder.

  private minimapPx(e: PointerEvent): { x: number; y: number } {
    const r = this.canvas.getBoundingClientRect();
    const cxyBorder = this.surf?.cxyBorder ?? 1;
    const x = Math.trunc((e.clientX - r.left) / this.d.magnify) - cxyBorder;
    const y = Math.trunc((e.clientY - r.top) / this.d.magnify) - cxyBorder;
    return { x, y };
  }

  private readonly onPointerDown = (e: PointerEvent): void => {
    if (this.last === null) return;
    e.preventDefault();
    this.canvas.setPointerCapture(e.pointerId);
    const { x, y } = this.minimapPx(e);
    this.pen = { xPenDown: 0, yPenDown: 0, wxViewDown: 0, wyViewDown: 0 };
    const wpt = minimapPenDown(this.last.input, x, y, this.pen);
    this.d.setViewPos(wpt.wx, wpt.wy);
  };

  private readonly onPointerMove = (e: PointerEvent): void => {
    if (this.pen === null || this.last === null) return;
    e.preventDefault();
    const { x, y } = this.minimapPx(e);
    const wpt = minimapPenMove(this.last.input, x, y, this.pen);
    this.d.setViewPos(wpt.wx, wpt.wy);
  };

  private readonly onPointerUp = (e: PointerEvent): void => {
    if (this.pen === null) return;
    try {
      this.canvas.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    this.pen = null;
  };
}
